/**
 * Schema evolution for the event contract.
 *
 * Events are immutable and the archive keeps them for years, so version 1 records will still be
 * read long after the shape has moved on. Readers therefore never parse a raw record directly:
 * they upgrade it to the current version first, and every version bump ships an upgrader here
 * along with a test that pins the old shape.
 */

export const CURRENT_SCHEMA_VERSION = 1 as const;

export type Upgrader = (event: Record<string, unknown>) => Record<string, unknown>;

/**
 * Keyed by the version being upgraded from. An entry under key 1 turns a version 1 record into a
 * version 2 record.
 */
export const UPGRADERS: Readonly<Record<number, Upgrader>> = Object.freeze({});

export class EventVersionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EventVersionError';
  }
}

/** Apply upgraders in order until the record is at the current version. */
export function upgradeToCurrent(raw: unknown): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new EventVersionError('event must be an object');
  }

  let event = { ...(raw as Record<string, unknown>) };
  const version = event['schema_version'];

  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new EventVersionError('event is missing a valid schema_version');
  }

  if (version > CURRENT_SCHEMA_VERSION) {
    // A newer writer than this reader. Guessing at an unknown shape is worse than stopping.
    throw new EventVersionError(
      `event schema_version ${version} is newer than this build understands (${CURRENT_SCHEMA_VERSION})`,
    );
  }

  for (let v = version; v < CURRENT_SCHEMA_VERSION; v += 1) {
    const upgrade = UPGRADERS[v];
    if (!upgrade) {
      throw new EventVersionError(`no upgrader registered from schema_version ${v}`);
    }
    event = upgrade(event);
    event['schema_version'] = v + 1;
  }

  return event;
}
